import React, { useState } from 'react';
import { X, Download, List } from 'lucide-react';

interface PdfReferenceNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (referenceNote: string, showBrandChecklist?: boolean) => void | Promise<void>;
  isGenerating: boolean;
  showBrandChecklistOption?: boolean;
  error?: string;
}

const PdfReferenceNoteModal: React.FC<PdfReferenceNoteModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
  showBrandChecklistOption = false,
  error,
}) => {
  const [referenceNote, setReferenceNote] = useState('');
  const [showBrandChecklist, setShowBrandChecklist] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await onGenerate(referenceNote, showBrandChecklistOption ? showBrandChecklist : undefined);
  };

  const handleClose = () => {
    setReferenceNote('');
    setShowBrandChecklist(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={() => !isGenerating && handleClose()}
    >
      <div
        className="bg-slate-900 rounded-lg shadow-2xl max-w-md w-full mx-4 border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Generate PDF Report</h2>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="text-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="referenceNote" className="block text-sm font-medium text-slate-300 mb-2">
              Reference Note (Optional)
            </label>
            <textarea
              id="referenceNote"
              value={referenceNote}
              onChange={(e) => setReferenceNote(e.target.value)}
              disabled={isGenerating}
              className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#AD0000] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              rows={4}
              placeholder="Add a reference note for this report..."
            />
          </div>

          {/* Brand Checklist Option */}
          {showBrandChecklistOption && (
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={showBrandChecklist}
                onChange={(e) => setShowBrandChecklist(e.target.checked)}
                disabled={isGenerating}
                className="w-4 h-4 rounded border-white/20 bg-slate-800 text-[#AD0000] focus:ring-[#AD0000] focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center gap-2">
                <List className="w-4 h-4 text-slate-400 group-hover:text-slate-300 transition-colors" />
                <span className="text-sm text-slate-300 group-hover:text-slate-200 transition-colors">
                  Include full brand checklist
                </span>
              </div>
            </label>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-500/40 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isGenerating}
              className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-all border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isGenerating}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-red-500/20 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Generate PDF</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PdfReferenceNoteModal;
